//@flow
import React from 'react';
import PriceToggle from './PriceToggle.react';
import ProgressBar from '../../HelloBar/components/ProgressBar.react';
import ShippingPrice from '../../HelloBar/components/ShippingPrice.react';
import IconClose from '../../Icon/components/IconClose.react';
import CartItems from './CartItems.react';
import CartActions from '../../Actions/cart';
import CheckoutButton from './CheckoutButton.react';
import MsrpToggle from './MsrpToggle.react';
import { Link, browserHistory } from 'react-router';
import { toggleFreezeBody } from '../../Utils/device';
import UrlData from '../../Utils/url-data';
import LoadingSpinner from '../../LoadingButton/components/LoadingSpinnerWrapper.react';

import type QuoteModel from '../../Stores/Quote/quoteModel';
import type QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import type QuoteTotalsModel from '../../Stores/Quote/quoteTotalsModel';
import type ProductsCollection from '../../Stores/Products/productsCollection';
import type MembershipModel from '../../Stores/Membership/membershipModel';
import type MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';
import type CustomerModel from '../../Stores/Customer/customerModel';
import type StockCollection from '../../Stores/Stock/stockCollection';

type Props = {
  quote:QuoteModel,
  products:ProductsCollection,
  quoteItems:QuoteItemsCollection,
  quoteTotals:QuoteTotalsModel,
  membership:MembershipModel,
  isCartOpen:boolean,
  isIOS:boolean,
  membershipPlan:MembershipPlanModel,
  customer:CustomerModel,
  showGiftUI:boolean,
  stock:StockCollection,
  hidePromoCodeInput:boolean,
  hideMsrpToggle:boolean,
  hideMembershipOption:boolean,
};

type State = {
  summaryOpen:boolean,
};

export default class CartPreview extends React.Component<Props, State> {
  state = {
    summaryOpen: false,
  };

  componentDidUpdate(prevProps:Props) {
    //For IOS, we need to add css to the root DIV when the cart preview is open to force the
    //nav bar to appear, so that our buttons aren't in the IOS dead zone of the bottom 44px.
    const reactBase = document.getElementById('react-section');
    if (!reactBase) return;
    if (this.props.isCartOpen && this.props.isCartOpen !== prevProps.isCartOpen) {
      toggleFreezeBody();
      if (prevProps.isIOS) return reactBase.classList.add('ios-show-nav');
    }
    if (!this.props.isCartOpen && this.props.isCartOpen !== prevProps.isCartOpen) {
      toggleFreezeBody();
      if (prevProps.isIOS) return reactBase.classList.remove('ios-show-nav');
    }
  }

  toggleSummary = () => {
    this.setState({ summaryOpen: !this.state.summaryOpen });
  }

  closeSummary = () => {
    if (!this.state.summaryOpen) return;
    this.setState({ summaryOpen: false });
  }

  _backToShopping() {
    CartActions.closeCartPreview();
    browserHistory.push('/products');
  }

  _closeCartPreview() {
    CartActions.closeCartPreview();
  }

  _cartEmpty():boolean {
    return !this.props.quoteItems.models.length;
  }

  _renderCartItems() {
    if (this._cartEmpty()) {
      return (
        <div className="empty-cart">
          <div className="head-text">
            Your cart is empty
          </div>
          <button onClick={this._backToShopping} className="cta hide-sm">
            Start shopping
          </button>
        </div>
      );
    }
    return (
      <CartItems
        inCart={true}
        products={this.props.products}
        quoteItems={this.props.quoteItems}
        quote={this.props.quote}
        summaryOpen={this.state.summaryOpen}
        closeSummary={this.closeSummary}
        lessTextMsrp={true}
        strikeUnselectedPrice={true}
        stock={this.props.stock}
        membershipPlan={this.props.membershipPlan}
      />
    );
  }

  _getMsrpToggle() {
    const memberSavings = this.props.quoteTotals.getMsrpMemberSavings()|| 0;

    const shouldHide = this.state.summaryOpen ||
      this.props.membership.isReturningMember() || !memberSavings ||
      this.props.membershipPlan.hideMembershipInfo() || this.props.hideMsrpToggle;

    if (shouldHide) return null;

    return (
      <MsrpToggle
        className="price-toggle-row"
        membershipDiscount={memberSavings}
        quoteId={this.props.quote.getId()}
        isMsrp={this.props.quote.isMsrp()}
      />
    );
  }

  _getButtons() {
    if (this._cartEmpty()) {
      return null;
    }

    return (
      <div className="bottom-content-holder">
        <PriceToggle
          className="price-toggle-row"
          quoteTotals={this.props.quoteTotals}
          newMember={!this.props.membership.isReturningMember()}
          membershipPlan={this.props.membershipPlan}
          quote={this.props.quote}
          summaryOpen={this.state.summaryOpen}
          toggleSummary={this.toggleSummary}
          showGiftUI={this.props.showGiftUI}
          hidePromoCodeInput={this.props.hidePromoCodeInput}
          hideMembershipOption={this.props.hideMembershipOption}
        />
        {this._getMsrpToggle()}
        <div className="button-holder">
          <CheckoutButton
            quote={this.props.quote}
            quoteItems={this.props.quoteItems}
            newMember={!this.props.membership.isReturningMember()}
            customer={this.props.customer}
            id="CartPreview-checkout-button"
            shouldIncludeQueryParams={true}
          >
            Checkout
          </CheckoutButton>
          <Link to={UrlData.makeUrlWithQuery('/cart')}>
            <button className="cta-secondary-color hide-sm">
              View Cart
            </button>
          </Link>
          <button onClick={this._backToShopping} className="cta-secondary-color hide-md-up">
            Keep shopping
          </button>
        </div>
      </div>
    );
  }

  render() {
    const freeShippingThreshold = this.props.quoteTotals.getMinimumFreeShipping();
    //For virtual quotes, make sure that the currentAmount = shippingThreshold because they have free shipping
    const currentAmount = this.props.quote.isVirtual() ? freeShippingThreshold : this.props.quoteTotals.getSubtotal();
    return (
      <div className="cart-preview scrollable" id="cart-preview">
        <div className="clearfix price-container">
          <div className="truck-icon"></div>
          <ShippingPrice
            quoteTotals={this.props.quoteTotals}
            inUS={true}
          />
          <IconClose closeAction={this._closeCartPreview}/>
        </div>
        <ProgressBar
          goalAmount={freeShippingThreshold}
          currentAmount={currentAmount || 0}
        />
        <LoadingSpinner />
        {this._renderCartItems()}
        {this._getButtons()}
      </div>
    );
  }
}
